import { Grid, Skeleton, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { AreaChart, InfoCard, MiniBarChart } from "src/design";
import { colorTextMuted } from "src/design/tokens";
import {
  useBookingsTrend,
  usePopularTimes,
} from "src/data/admin/queries/analytics";
import { DateRangeParams } from "src/data/admin/types/analytics";
import { CardLink } from "./CardLink";

/** The dashboard always looks back a fixed month; finer ranges live on Analytics. */
export const ANALYTICS_WINDOW_DAYS = 30;

interface DashboardAnalyticsCardProps {
  restaurantId: string;
}

/** Height `AreaChart` gives its plot. */
const CHART_HEIGHT = 250;

/**
 * Height of the peak-hours strip, chosen so its bars sit on the same line as
 * the area chart's x-axis rather than hanging below it.
 *
 * `AreaChart` puts its axis 209px below its own top edge — its 250px plot less
 * the band reserved for the angled date labels and recharts' margins — and the
 * strip adds its hour labels under the bars. Measured against the rendered
 * chart rather than derived, since it depends on recharts' internal layout;
 * if `AreaChart`'s plot height or axis angle changes, re-measure this.
 */
const PEAK_STRIP_HEIGHT = 231;

/** Identical caption over each chart, so both bodies start on the same line. */
const ChartHeading = ({ label }: { label: string }) => (
  <Typography sx={{ fontSize: "12.5px", color: colorTextMuted, mb: 1 }}>
    {label}
  </Typography>
);

export const DashboardAnalyticsCard = ({
  restaurantId,
}: DashboardAnalyticsCardProps) => {
  const { t } = useTranslation();

  const range: DateRangeParams = {
    restaurantId,
    startDate: dayjs()
      .subtract(ANALYTICS_WINDOW_DAYS - 1, "day")
      .format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  };

  const { data: trend, isLoading: trendLoading } = useBookingsTrend(range);
  const { data: popularTimes, isLoading: timesLoading } =
    usePopularTimes(range);

  const bookingsKey = t("dashboard.analytics.bookings");
  const guestsKey = t("dashboard.analytics.guests");

  const trendData = (trend ?? []).map((point) => ({
    name: point.date,
    [bookingsKey]: point.bookings,
    [guestsKey]: point.guests,
  }));

  // Only hours that actually took bookings: the endpoint reports every hour of
  // the day, and a full set of labels under mostly-empty bars is unreadable at
  // this width. Dropping the empties keeps the remaining hours legible.
  const popularTimesData = (popularTimes ?? [])
    .filter((point) => point.count > 0)
    .map((point) => ({
      label: `${point.hour}h`,
      value: point.count,
    }));

  // Thin the ticks so the daily labels never collide across a month.
  const tickInterval = Math.max(0, Math.ceil(trendData.length / 8) - 1);

  return (
    <InfoCard
      title={t("dashboard.analytics.title")}
      subtitle={t("dashboard.analytics.subtitle", {
        days: ANALYTICS_WINDOW_DAYS,
      })}
      action={
        <CardLink
          to={`/restaurant/${restaurantId}/analytics`}
          label={t("dashboard.details")}
        />
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <ChartHeading label={t("dashboard.analytics.trendTitle")} />
          {trendLoading ? (
            <Skeleton variant="rounded" height={CHART_HEIGHT} />
          ) : (
            <AreaChart
              data={trendData}
              dataKeys={[bookingsKey, guestsKey]}
              xAxisTickFormatter={(value) => dayjs(value).format("D MMM")}
              xAxisInterval={tickInterval}
              xAxisAngle={-35}
              compact
            />
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartHeading label={t("dashboard.analytics.peakHours")} />
          {timesLoading ? (
            <Skeleton variant="rounded" height={PEAK_STRIP_HEIGHT} />
          ) : (
            <MiniBarChart
              data={popularTimesData}
              height={PEAK_STRIP_HEIGHT}
              formatValue={({ value }) =>
                t("dashboard.analytics.bookingsCount", { count: value })
              }
            />
          )}
        </Grid>
      </Grid>
    </InfoCard>
  );
};
